/**
 * Purpose: React context provider that applies theme colors as CSS custom properties.
 * Owns: Theme state, CSS variable injection on :root, theme switching.
 * Notes: Every component reads colors from CSS vars (--bg-chalkboard, --btn-a, etc.)
 *        so swapping themes at the pack level repaints the entire UI automatically.
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Theme } from '../types/theme';
import { getTheme, DEFAULT_THEME_ID } from './themes';

interface ThemeContextValue {
  /** The currently active theme object */
  theme: Theme;
  /** Switch to a different theme by ID */
  setThemeId: (id: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Maps a Theme object's color tokens to CSS custom properties on :root.
 * Property names match conventions.md section 3 (e.g., --bg-chalkboard).
 */
function applyThemeToDocument(theme: Theme): void {
  const root = document.documentElement;
  root.style.setProperty('--bg-chalkboard', theme.colors.bgChalkboard);
  root.style.setProperty('--bg-card', theme.colors.bgCard);
  root.style.setProperty('--btn-a', theme.colors.btnA);
  root.style.setProperty('--btn-b', theme.colors.btnB);
  root.style.setProperty('--btn-c', theme.colors.btnC);
  root.style.setProperty('--btn-d', theme.colors.btnD);
  root.style.setProperty('--accent-wood', theme.colors.accentWood);
  root.style.setProperty('--text-light', theme.colors.textLight);
  root.style.setProperty('--text-dark', theme.colors.textDark);
  root.style.setProperty('--accent-gold', theme.colors.accentGold);
  root.style.setProperty('--correct', theme.colors.correct);
  root.style.setProperty('--incorrect', theme.colors.incorrect);
  root.style.setProperty('--bg-surface', theme.colors.bgSurface);
  root.style.setProperty('--muted', theme.colors.muted);
  root.style.setProperty('--font-family', theme.fontFamily);
  root.style.setProperty('--font-family-display', theme.fontFamilyDisplay);
  root.style.setProperty('--font-family-handwriting', theme.fontFamilyHandwriting);
}

interface ThemeProviderProps {
  children: ReactNode;
  /** Optional initial theme ID. Defaults to the classroom theme. */
  initialThemeId?: string;
}

export function ThemeProvider({ children, initialThemeId }: ThemeProviderProps) {
  const [themeId, setThemeId] = useState(initialThemeId ?? DEFAULT_THEME_ID);
  const theme = useMemo(() => getTheme(themeId), [themeId]);

  /* Apply CSS custom properties whenever the theme changes */
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setThemeId }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Hook to access the current theme and theme switcher. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a <ThemeProvider>');
  }
  return ctx;
}
